import { useCallback, useMemo, useRef, useState } from 'react';

import { searchContext, userAppContext } from '@/context/Context';
import { getAllColumns } from '@/components/Table/columns/index';
import { fetchGdaEvidencesWithTimeline } from '@/services/evidenceTimelineService';
import { useMappingStackStore } from '@/store';
import { useEvidenceTimelineStore } from '@/store/evidenceTimelineStore';
import { buildNumPmidMappingConfig } from '@/utils/evidenceTimelineMapping';
import type { MappingLevelContent } from '@/types/mappingDrillDown';

import './EvidenceTimeline.css';

type EvidenceByYearItem = {
  year: number;
  evidence: number;
};

type TimelineItem = {
  pmYear: number;
  citationCount: number;
};

export type EvidenceTimelinePoint = {
  year: number;
  evidence: number;
  kind: 'major' | 'mini';
  hiddenMiniPoints?: number;
};

type ReferenceFilterType = 'PMID' | 'NCTID' | 'ALL';

function readReferenceFilter(rowData: Record<string, unknown>): ReferenceFilterType {
  const value = rowData?._referenceFilter;
  return value === 'PMID' || value === 'NCTID' || value === 'ALL' ? value : 'ALL';
}

type EvidenceTimelineProps = {
  timeline?: TimelineItem[] | null;
  publicationsByYear?: unknown;
  rowData: Record<string, unknown>;
  className?: string;
};

function toPlainText(node: unknown): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toPlainText).join('');
  if (typeof node === 'object' && node !== null && 'props' in (node as any)) {
    return toPlainText((node as { props?: { children?: unknown } }).props?.children);
  }
  return '';
}

function normalizeTimeline(
  timeline: TimelineItem[] | null | undefined,
  maxYearInclusive: number,
): EvidenceByYearItem[] {
  if (!Array.isArray(timeline)) return [];
  return timeline
    .filter(
      it =>
        it &&
        Number.isFinite(Number(it.pmYear)) &&
        Number.isFinite(Number(it.citationCount)) &&
        Number(it.pmYear) <= maxYearInclusive,
    )
    .map(it => ({ year: Number(it.pmYear), evidence: Number(it.citationCount) }))
    .sort((a, b) => a.year - b.year);
}

const YEARS_PER_BLOCK = 5;

function buildEvidenceYearMap(items: EvidenceByYearItem[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const it of items) {
    const y = Number(it.year);
    const e = Number(it.evidence);
    if (!Number.isFinite(y)) continue;
    m.set(y, Number.isFinite(e) ? e : 0);
  }
  return m;
}

function buildTimelinePoints(evidenceByYear: EvidenceByYearItem[], maxYearInclusive: number) {
  if (evidenceByYear.length === 0) {
    return { minYear: 0, maxYear: 0, points: [] as EvidenceTimelinePoint[] };
  }

  const minYear = evidenceByYear[0].year;
  const dataMaxYear = evidenceByYear[evidenceByYear.length - 1].year;
  const maxYear = Math.min(dataMaxYear, maxYearInclusive);
  if (minYear > maxYear) {
    return { minYear: 0, maxYear: 0, points: [] as EvidenceTimelinePoint[] };
  }

  const yearToEvidence = buildEvidenceYearMap(evidenceByYear);

  const points: EvidenceTimelinePoint[] = [];
  let axisMaxYear = maxYear;

  let majorYear = minYear;
  while (majorYear <= maxYear) {
    for (let i = 0; i < YEARS_PER_BLOCK; i++) {
      const y = majorYear + i;
      if (y > maxYearInclusive) break;

      const evidence = yearToEvidence.get(y) ?? 0;
      if (i === 0) {
        points.push({
          year: y,
          evidence,
          kind: 'major',
          hiddenMiniPoints: 0,
        });
      } else {
        points.push({ year: y, evidence, kind: 'mini' });
      }
    }
    axisMaxYear = Math.max(axisMaxYear, Math.min(majorYear + YEARS_PER_BLOCK - 1, maxYearInclusive));
    majorYear += YEARS_PER_BLOCK;
  }

  points.sort((a, b) => a.year - b.year);
  return { minYear, maxYear: axisMaxYear, points };
}

function formatEvidence(evidence: number) {
  return evidence.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function pointRenderKey(p: EvidenceTimelinePoint) {
  return `${p.kind}-${p.year}-${p.evidence}`;
}

function filterButtonClassName(isActive: boolean) {
  return `evidence-timeline__filterButton ${isActive ? 'evidence-timeline__filterButton--primary' : 'evidence-timeline__filterButton--secondary'}`;
}

const CURRENT_YEAR = new Date().getFullYear();

export default function EvidenceTimeline({ timeline, rowData, className }: EvidenceTimelineProps) {
  const normalized = useMemo(() => normalizeTimeline(timeline, CURRENT_YEAR), [timeline]);
  const { points } = useMemo(() => buildTimelinePoints(normalized, CURRENT_YEAR), [normalized]);

  const peakEvidence = useMemo(() => {
    if (normalized.length === 0) return 0;
    return Math.max(...normalized.map(x => x.evidence));
  }, [normalized]);

  const {
    view = '',
    tab = '',
    source = '',
    searchItems = [],
    setMappingContent,
  } = (searchContext() ?? {}) as {
    view?: string;
    tab?: string;
    source?: string;
    searchItems?: Array<{ chemID?: string; chemPrefName?: string }>;
    setMappingContent?: (content: Record<string, unknown> | (() => Record<string, unknown>)) => void;
  };
  const userCtx = userAppContext() as {
    authorization?: unknown;
    setAuthError?: (e: unknown) => void;
  };
  const authorization = userCtx?.authorization;
  const setAuthError = userCtx?.setAuthError ?? (() => {});
  const setEvidenceTimelineRow = useEvidenceTimelineStore(s => s.setEvidenceTimelineRow);
  const pushLevel = useMappingStackStore(s => s.pushLevel);
  const useDrillDownMapping = process.env.REACT_APP_USE_DRILLDOWN_MAPPING === 'true';
  const [referenceFilter, setReferenceFilter] = useState<ReferenceFilterType>(() =>
    readReferenceFilter(rowData),
  );
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const referenceFilterRequestSeq = useRef(0);

  const numPmidColumn = getAllColumns({ isEvidence: true, tab }).find(
    (c: { name?: string }) => c.name === 'numPMIDsupportingAssociation',
  );

  const openEvidenceMapping = useCallback(
    (referenceType: ReferenceFilterType, year?: number) => {
      const config = buildNumPmidMappingConfig(rowData, view, tab, searchItems ?? []);
      if (!config) return;

      const defaultLabelByReference: Record<ReferenceFilterType, string> = {
        PMID: 'N PMIDs',
        NCTID: 'N NCTIDs',
        ALL: 'ALL',
      };
      const fallbackLabel = defaultLabelByReference[referenceType];
      const levelLabel = toPlainText(numPmidColumn?.header).trim() || fallbackLabel;
      const levelLabelNode =
        referenceType === 'NCTID'
          ? 'N NCTIDs'
          : referenceType === 'ALL'
            ? 'ALL'
            : (numPmidColumn?.header ?? levelLabel);

      const mappingPayload: MappingLevelContent = {
        ...config,
        selectedReferenceType: referenceType,
        ...(year != null ? { selectedPmYear: year } : {}),
      };

      const drilldownLabel = year != null ? `${levelLabel} · ${year}` : `${referenceType}`;

      if (useDrillDownMapping) {
        pushLevel({
          id: `level-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          label: drilldownLabel,
          labelNode: year != null ? levelLabelNode : referenceType,
          isBase: false,
          mappingContent: mappingPayload,
        });
      } else if (typeof setMappingContent === 'function') {
        setMappingContent(() => ({
          isActive: true,
          ...mappingPayload,
        }));
      }
    },
    [numPmidColumn?.header, pushLevel, rowData, searchItems, setMappingContent, tab, useDrillDownMapping, view],
  );

  const hasTopReferenceFilter = referenceFilter !== 'ALL';

  const applyReferenceFilter = useCallback(
    (filterType: ReferenceFilterType) => {
      setReferenceFilter(filterType);
      setSelectedYear(null);
      openEvidenceMapping(filterType);

      const diseaseFilter = String(rowData?.diseaseUMLSCUI ?? '').replace(/^UMLS_/, '');
      const variantName = String(rowData?.variantStrID ?? rowData?.strID ?? '');
      const canFetchTimeline =
        Boolean(source) &&
        Boolean(diseaseFilter) &&
        (tab === 'GDA' ? Boolean(rowData?.geneSymbol) : tab === 'VDA' ? Boolean(variantName) : false);

      if (!canFetchTimeline) return;

      const seq = ++referenceFilterRequestSeq.current;
      setEvidenceTimelineRow({
        ...rowData,
        _referenceFilter: filterType,
        _evidenceTimelineLoading: true,
        _evidenceTimelineError: false,
      });

      void fetchGdaEvidencesWithTimeline({
        source: source as string,
        diseaseFilter,
        page_number: 0,
        page_size: 50,
        authorization,
        setAuthError,
        tab: tab.toLowerCase(),
        referenceType: filterType,
        ...(tab === 'VDA' ? { variantName } : { geneSymbol: String(rowData.geneSymbol) }),
      })
        .then(result => {
          if (seq !== referenceFilterRequestSeq.current) return;

          const open = useEvidenceTimelineStore.getState().evidenceTimelineRow;
          if (!open) return;

          setEvidenceTimelineRow({
            ...open,
            _referenceFilter: filterType,
            timeline: result.timeline ?? undefined,
            publicationsByYear: result.publicationsByYear ?? undefined,
            _evidenceTimelineLoading: false,
            _evidenceTimelineError: false,
          });
        })
        .catch(() => {
          if (seq !== referenceFilterRequestSeq.current) return;

          const open = useEvidenceTimelineStore.getState().evidenceTimelineRow;
          if (!open) return;

          setEvidenceTimelineRow({
            ...open,
            _evidenceTimelineLoading: false,
            _evidenceTimelineError: true,
          });
        });
    },
    [authorization, openEvidenceMapping, rowData, setAuthError, setEvidenceTimelineRow, source, tab],
  );

  const handlePointClick = useCallback(
    (year: number) => {
      if (hasTopReferenceFilter) {
        setSelectedYear(null);
        openEvidenceMapping(referenceFilter, year);
        return;
      }
      setSelectedYear(prev => (prev === year ? null : year));
    },
    [hasTopReferenceFilter, openEvidenceMapping, referenceFilter],
  );

  const PAD = 12;
  const STEP_PX = 48;

  const virtualHeight = useMemo(() => {
    const n = points.length;
    if (n === 0) return 200;
    return 2 * PAD + n * STEP_PX;
  }, [points.length]);

  const centerYByIndex = useMemo(() => {
    const n = points.length;
    const map = new Map<string, number>();
    for (let i = 0; i < n; i++) {
      const p = points[i];
      map.set(pointRenderKey(p), PAD + STEP_PX / 2 + i * STEP_PX);
    }
    return map;
  }, [points]);

  const lineEnds = useMemo(() => {
    const n = points.length;
    if (n === 0) return { top: PAD, height: 0 };
    const firstY = PAD + STEP_PX / 2;
    const lastY = PAD + STEP_PX / 2 + (n - 1) * STEP_PX;
    return { top: firstY, height: Math.max(4, lastY - firstY) };
  }, [points.length]);

  return (
    <div className={`evidence-timeline ${className ?? ''}`}>
      <div className="evidence-timeline__legendBlock">
        <div
          className="evidence-timeline__filterPanel_top"
          role="group"
          aria-label="Reference type filters for evidence timeline"
        >
          <div className="evidence-timeline__filterButtons">
            <button
              type="button"
              className={filterButtonClassName(referenceFilter === 'PMID')}
              onClick={() => applyReferenceFilter('PMID')}
            >
              PMIDs
            </button>
            <button
              type="button"
              className={filterButtonClassName(referenceFilter === 'NCTID')}
              onClick={() => applyReferenceFilter('NCTID')}
            >
              NCTID
            </button>
            <button
              type="button"
              className={filterButtonClassName(referenceFilter === 'ALL')}
              onClick={() => applyReferenceFilter('ALL')}
            >
              ALL
            </button>
          </div>
        </div>
      </div>

      <div className="evidence-timeline__canvasWrap">
        <div className="evidence-timeline__canvasVirtual" style={{ height: virtualHeight }}>
          <div
            className="evidence-timeline__line"
            style={{
              top: lineEnds.top,
              height: lineEnds.height,
              bottom: 'auto',
            }}
          />

          {points.map(p => {
            const y = centerYByIndex.get(pointRenderKey(p)) ?? PAD;
            const isPeakYear = peakEvidence > 0 && p.evidence === peakEvidence;
            const isSelectedYear = !hasTopReferenceFilter && selectedYear === p.year;

            if (p.kind === 'major') {
              return (
                <button
                  key={`major-${p.year}-${p.evidence}`}
                  type="button"
                  className={`evidence-timeline__point evidence-timeline__point--major${isPeakYear ? ' evidence-timeline__point--peak' : ''}${isSelectedYear ? ' is-selected' : ''}`}
                  style={{ top: y }}
                  onClick={e => {
                    e.stopPropagation();
                    handlePointClick(p.year);
                  }}
                  aria-label={
                    hasTopReferenceFilter
                      ? `Open evidence table for year ${p.year}`
                      : `Open filter options for year ${p.year}`
                  }
                  title={
                    hasTopReferenceFilter
                      ? `Year ${p.year}: open evidence table`
                      : `Year ${p.year}: choose evidence filter`
                  }
                >
                  <span className="evidence-timeline__majorDotText">{formatEvidence(p.evidence)}</span>
                  <span className="evidence-timeline__majorYearLabel">
                    {String(p.year).replace(/,/g, '')}
                  </span>
                </button>
              );
            }

            return (
              <button
                key={`mini-${p.year}-${p.evidence}`}
                type="button"
                className={`evidence-timeline__point evidence-timeline__point--mini${isPeakYear ? ' evidence-timeline__point--peak' : ''}${isSelectedYear ? ' is-selected' : ''}`}
                style={{ top: y }}
                onClick={e => {
                  e.stopPropagation();
                  handlePointClick(p.year);
                }}
                aria-label={
                  hasTopReferenceFilter
                    ? `Open evidence table for year ${p.year}`
                    : `Open filter options for year ${p.year}`
                }
                title={
                  hasTopReferenceFilter
                    ? `Year ${p.year}: open evidence table`
                    : `Year ${p.year}: choose evidence filter`
                }
              >
                <span className="evidence-timeline__miniDot"> {formatEvidence(p.evidence)}</span>
                <span className="evidence-timeline__miniYearLabel" aria-hidden="true">
                  {String(p.year).replace(/,/g, '')}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {!hasTopReferenceFilter && selectedYear != null && (
        <div
          className="evidence-timeline__filterPanel"
          role="group"
          aria-label={`Filters for year ${selectedYear}`}
        >
          <div className="evidence-timeline__filterPanelTitle">
            Year <strong>{selectedYear}</strong> - choose evidence type
          </div>
          <div className="evidence-timeline__filterButtons">
            <button
              type="button"
              className="evidence-timeline__filterButton evidence-timeline__filterButton--primary"
              onClick={() => openEvidenceMapping('PMID', selectedYear)}
            >
              PMIDs
            </button>
            <button
              type="button"
              className="evidence-timeline__filterButton evidence-timeline__filterButton--secondary"
              onClick={() => openEvidenceMapping('NCTID', selectedYear)}
            >
              NCTID
            </button>
            <button
              type="button"
              className="evidence-timeline__filterButton evidence-timeline__filterButton--ghost"
              onClick={() => openEvidenceMapping('ALL', selectedYear)}
            >
              ALL
            </button>
          </div>
        </div>
      )}
      <div className="mt-2 text-center">
        <small className="evidence-timeline__legend">
          {hasTopReferenceFilter ? (
            <>
              Click a dot to open the <strong>Evidence</strong> table for that publication year.
            </>
          ) : (
            <>
              Click a dot and choose a filter to open the <strong>Evidence</strong> table for that
              publication year.
            </>
          )}
        </small>
        <div className="divider my-2"></div>
        {peakEvidence > 0 && (
          <p className="evidence-timeline__legend evidence-timeline__legend--peak">
            <span className="evidence-timeline__legendPeakSwatch" aria-hidden />
            <span>
              The dot with a soft pink glow marks the year with the most publications in this timeline.
            </span>
          </p>
        )}
      </div>
    </div>
  );
}
